import React, { useRef, useEffect, useState } from 'react';
import {
  Paperclip, Type, Heading2, Bold, Italic,
  List, ListOrdered, Image as ImageIcon, Video, Link2, Quote, Code,
} from 'lucide-react';
import { uploadFile } from '../services/api';

/**
 * Lightweight rich-text editor with a bottom toolbar.
 * Emits HTML via onChange. `author` renders a small header (avatar + name).
 * The attachment and image buttons open the local file picker and upload.
 */
export default function RichTextEditor({ value, onChange, author, placeholder }) {
  const ref = useRef(null);
  const initialized = useRef(false);
  const savedRange = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Seed the editor with the initial HTML (the template), and re-sync when
  // `value` is changed externally (e.g. the form resets after submit) — but
  // never while the user is actively typing, to avoid cursor jumps.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!initialized.current || (document.activeElement !== el && el.innerHTML !== (value || ''))) {
      el.innerHTML = value || '';
      initialized.current = true;
    }
  }, [value]);

  const emit = () => { if (ref.current) onChange(ref.current.innerHTML); };

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };
  const block = (tag) => exec('formatBlock', tag);

  const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // Remember where the caret was, since opening a file dialog / prompt blurs the editor.
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    } else {
      savedRange.current = null;
    }
  };

  const insertHTMLAtSaved = (html) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else {
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false); // caret at end
      sel.removeAllRanges();
      sel.addRange(r);
    }
    document.execCommand('insertHTML', false, html);
    savedRange.current = null;
    emit();
  };

  const addLink = () => { saveSelection(); const u = window.prompt('Enter URL'); if (u) insertHTMLAtSaved(`<a href="${esc(u)}" target="_blank" rel="noopener noreferrer">${esc(u)}</a>`); };
  const addVideo = () => { saveSelection(); const u = window.prompt('Video / embed link URL'); if (u) insertHTMLAtSaved(`<a href="${esc(u)}" target="_blank" rel="noopener noreferrer">🎥 ${esc(u)}</a>`); };

  const pickImage = () => { saveSelection(); imageInputRef.current?.click(); };
  const pickFile = () => { saveSelection(); fileInputRef.current?.click(); };

  const onPick = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await uploadFile(fd);
      const isImg = kind === 'image' || (file.type || '').startsWith('image/');
      if (isImg) {
        insertHTMLAtSaved(`<img src="${esc(data.url)}" alt="${esc(data.name)}" />`);
      } else {
        insertHTMLAtSaved(`<a href="${esc(data.url)}" target="_blank" rel="noopener noreferrer">📎 ${esc(data.name)}</a>`);
      }
    } catch (err) {
      window.alert(err?.response?.data?.error || 'Upload failed. Allowed: images, PDF, Office docs, txt/csv, zip (max 10 MB).');
    } finally {
      setUploading(false);
    }
  };

  const Btn = ({ onClick, title, children, disabled }) => (
    <button type="button" className="rte-btn" title={title} disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {children}
    </button>
  );
  const Sep = () => <span className="rte-sep" />;

  return (
    <div className="rte">
      {author && (
        <div className="rte-author">
          <span className="rte-avatar">{(author[0] || '?').toUpperCase()}</span>
          <span className="rte-author-name">{author}</span>
        </div>
      )}

      <div
        ref={ref}
        className="rte-content"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        data-placeholder={placeholder || 'Describe the issue…'}
      />

      {/* Hidden native file pickers */}
      <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onPick(e, 'image')} />
      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={(e) => onPick(e, 'file')} />

      <div className="rte-toolbar">
        <Btn onClick={pickFile} title="Attach a file (upload)" disabled={uploading}><Paperclip size={16} /></Btn>
        <Btn onClick={() => block('P')} title="Normal text"><Type size={16} /></Btn>
        <Btn onClick={() => block('H2')} title="Heading"><Heading2 size={16} /></Btn>
        <Sep />
        <Btn onClick={() => exec('bold')} title="Bold"><Bold size={16} /></Btn>
        <Btn onClick={() => exec('italic')} title="Italic"><Italic size={16} /></Btn>
        <Sep />
        <Btn onClick={() => exec('insertUnorderedList')} title="Bullet list"><List size={16} /></Btn>
        <Btn onClick={() => exec('insertOrderedList')} title="Numbered list"><ListOrdered size={16} /></Btn>
        <Sep />
        <Btn onClick={pickImage} title="Upload image" disabled={uploading}><ImageIcon size={16} /></Btn>
        <Btn onClick={addVideo} title="Insert video / embed link"><Video size={16} /></Btn>
        <Btn onClick={addLink} title="Insert link"><Link2 size={16} /></Btn>
        <Btn onClick={() => block('BLOCKQUOTE')} title="Quote"><Quote size={16} /></Btn>
        <Btn onClick={() => block('PRE')} title="Code block"><Code size={16} /></Btn>
        {uploading && <span className="rte-uploading">Uploading…</span>}
      </div>
    </div>
  );
}
